import { Button, Heading, Spacer, Switch, Tooltip } from '@sparemin/blockhead';
import React from 'react';

import AutomationPostModalBackButton from 'components/AutomationPostModalBackButton';
import ModalContentWithMedia from 'components/Modal/ModalContentWithMedia';
import { Step } from 'components/SteppedModal';
import { block } from '../utils';
import DisclosureSection from './DisclosureSection';
import useDisclosureStepHandler from './useDisclosureStepHandler';

export interface UseDisclosureStepProps {
  isDisclosureVisible: boolean;
  brandContent: boolean;
  brandOrganic: boolean;
  onBrandContentChange: (brandContent: boolean) => void;
  onBrandOrganicChange: (brandOrganic: boolean) => void;
  onBackClick: () => void;
  onHideModal: () => void;
  onVisibilityChange: (isDisclosureVisible: boolean) => void;
}

export default function useDisclosureStep({
  isDisclosureVisible,
  brandContent,
  brandOrganic,
  onBrandContentChange,
  onBrandOrganicChange,
  onBackClick: onBackClickProp,
  onHideModal,
  onVisibilityChange,
}: UseDisclosureStepProps): Step {
  const {
    isUntouched,
    localBrandContent,
    localBrandOrganic,
    onSetLocalBrandContent,
    onSetLocalBrandOrganic,
    onToggleDisclosureVisibility,
    onBackClick,
    onSaveChanges,
  } = useDisclosureStepHandler({
    isDisclosureVisible,
    brandContent,
    brandOrganic,
    onVisibilityChange,
    onBrandContentChange,
    onBrandOrganicChange,
    onBackClick: onBackClickProp,
  });

  return {
    id: 'disclosure',
    hideFooter: true,
    replaceBody: true,
    component: (
      <>
        <ModalContentWithMedia
          onClose={onHideModal}
          className="composer-step-content"
        >
          <ModalContentWithMedia.ModalContentWithMediaColumn>
            <img
              src="https://static.sparemin.com/static/webapp-assets/auto-posting-detail/auto-posting-detail-tik-tok-image-a-202403191010.png"
              className={block('composer-image-left')}
            />
          </ModalContentWithMedia.ModalContentWithMediaColumn>

          <ModalContentWithMedia.ModalContentWithMediaColumn className="composer-content-column">
            <Spacer
              orientation="vertical"
              align="center"
              justify="center"
              space={2.5}
              className={block('step-container')}
            >
              <AutomationPostModalBackButton
                text="Disclose video content"
                onClick={onBackClick}
              />

              <Spacer
                orientation="horizontal"
                align="center"
                justify="space-between"
                space="20px"
              >
                <Heading level={4} className={block('disclosure-description')}>
                  Turn on to disclose that your videos promote goods or services
                  in exchange for something of value. Your videos could promote
                  yourself, a third party, or both.
                </Heading>

                <Switch
                  isSelected={isDisclosureVisible}
                  onChange={onToggleDisclosureVisibility}
                />
              </Spacer>

              <Spacer
                orientation="vertical"
                align="flex-start"
                space="30px"
                className={block('disclosure-bottom')}
              >
                {isDisclosureVisible && (
                  <DisclosureSection
                    brandContent={localBrandContent ?? brandContent}
                    brandOrganic={localBrandOrganic ?? brandOrganic}
                    onBrandContentChange={onSetLocalBrandContent}
                    onBrandOrganicChange={onSetLocalBrandOrganic}
                  />
                )}

                <Tooltip
                  arrow
                  placement="top"
                  contents="You need to indicate if your content promotes yourself, a third party, or both."
                  isDisabled={!isUntouched}
                >
                  <Button
                    fluid
                    isDisabled={isUntouched}
                    onPress={onSaveChanges}
                  >
                    Save changes
                  </Button>
                </Tooltip>
              </Spacer>
            </Spacer>
          </ModalContentWithMedia.ModalContentWithMediaColumn>
        </ModalContentWithMedia>
      </>
    ),
  };
}
